import React, { ReactElement } from 'react';
import { TouchableOpacityProps } from 'react-native';
import styled from 'styled-components/native';

import Colors from '../../constants/Colors';
import ListItemAccessory from './ListItemAccessory';
import ListItemText from './ListItemText';

const StyledTouchableOpacity = styled.TouchableOpacity<ListItemProps>`
  position: relative;
  flex-direction: row;
  align-items: center;
  padding: ${p => `10px 12px`};
  border-radius: ${p => (p.framed ? 16 : 0)}px;
  border-width: ${p => (p.framed ? 2 : 0)}px;
  border-color: ${p => (p.framed ? Colors.listItemBorder : 'transparent')};
  border-bottom-width: ${p => (p.framed ? 2 : p.divider ? 1 : 0)}px;
  border-bottom-color: ${p =>
    p.framed
      ? Colors.listItemBorder
      : p.divider
      ? `${Colors.divider}`
      : 'transparent'};
  background-color: ${p =>
    typeof p.filled === 'string'
      ? p.filled
      : p.filled
      ? 'white'
      : 'transparent'};
` as React.FC<ListItemProps>;

export type ListItemProps = TouchableOpacityProps & {
  divider?: boolean;
  framed?: boolean;
  filled?: boolean | string; // if filled prop is provided without string color then use white and if color provided use the color
  size?: 'normal' | 'medium' | 'small' | number;
};

const ListItem: React.FC<ListItemProps> = ({
  divider,
  framed,
  children,
  onPress,
  ...props
}) => {
  // see if there is a child ListItemAccessory
  const hasItemAccessory = React.Children.toArray(children).some(
    (child: any) => child?.type?.name === ListItemAccessory.name
  );
  children = React.Children.map(children, child => {
    const element = child as ReactElement;
    if (!element) {
      return element;
    }
    const elementTypeName = (element.type as any).name;
    if (
      hasItemAccessory &&
      elementTypeName === ListItemText.name &&
      typeof element.props.accessoryPadding === 'undefined'
    ) {
      // Notify all ListItemText if accessory exists
      return React.cloneElement(element, {
        accessoryPadding: true,
        extendedDivider: divider
      });
    } else if (
      (divider || framed) &&
      (elementTypeName === ListItemText.name ||
        elementTypeName === ListItemAccessory.name)
    ) {
      // Notify ListItemText and ListItemAccessory components that
      // the list divider is extended edge to edge to set appropriate
      // padding
      return React.cloneElement(element, {
        extendedDivider: divider,
        framed
      });
    }
    return child;
  });
  return (
    <StyledTouchableOpacity
      activeOpacity={0.7}
      disabled={!onPress}
      divider={divider}
      framed={framed}
      onPress={onPress}
      {...props}
    >
      {children}
    </StyledTouchableOpacity>
  );
};

export default ListItem;
